import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import style from "./UpdateBidModal.module.scss";
import { Expeditor } from "../../../../../services";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import { CURRENCIES } from "../../../../../mock";
import HintIcon from "../../../../shared/icons/HintIcon";

const UpdateBidModal = ({ onRefreshOrder, isOpen, onClose, bid, order, onSuccess }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(bid?.currency || "KZT");
  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isAuction = order?.assignmentMethod === "Auction";
  const reductionStep = Number(order?.bidReductionStep);
  const hasReductionStep = isAuction && Number.isFinite(reductionStep) && reductionStep > 0;
  const currentMinPrice = Number(bid?.minPrice);
  const hasCurrentMinPrice = bid?.minPrice != null && Number.isFinite(currentMinPrice) && currentMinPrice > 0;
  const currencySymbol = currency === "KZT" ? "₸" : CURRENCIES[currency]?.symbol || currency;
  const stepPrice = hasReductionStep ? `${reductionStep.toLocaleString("ru-RU")} ${currencySymbol}` : "";

  if (!isOpen) return null;

  const handleClose = () => {
    setPrice("");
    setCurrency(bid?.currency || "KZT");
    setErrorText("");
    onClose();
  };

  const handleUpdate = async () => {
    const rounded = Math.round(Number(price));
    if (!rounded || rounded <= 0) {
      setErrorText(t("orderInfoExpeditor.bid.correctBidRequired"));
      return;
    }
    if (rounded > 2000000000) {
      setErrorText(t("orderInfoExpeditor.bid.bidTooLarge"));
      return;
    }
    if (isAuction && hasCurrentMinPrice && rounded >= currentMinPrice) {
      setErrorText(t("complexTender.updateBid.belowBestBidRequired"));
      return;
    }
    if (hasReductionStep && hasCurrentMinPrice && rounded > currentMinPrice - reductionStep) {
      setErrorText(t("orderInfoExpeditor.bid.reductionStepError", { step: stepPrice }));
      return;
    }

    setIsLoading(true);
    try {
      await Expeditor.updateOrderBid(bid.id, {
        orderId: bid.orderId,
        price: rounded,
        currency: currency,
        weight: bid.weight,
        volume: bid.volume,
        plannedDays: bid.plannedDays,
        comments: bid.comments,
      });
      toast.success(t("orderInfoExpeditor.bid.bidUpdated"));
      setPrice("");
      setErrorText("");
      if (onSuccess) onSuccess();
    } catch (error) {
      await requestErrorDisplay(error, t, "orderInfoExpeditor.bid.updateBidError");
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("orderInfoExpeditor.bid.changeBid")}</span>
          <button className={style.close_btn} onClick={handleClose} aria-label={t("orderInfoExpeditor.assignDriverModal.close")}>
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>
        <div className={style.body}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={t("orderInfoExpeditor.bid.newBidLabel")}
            placeholder={t("orderInfoExpeditor.bid.yourBidPlaceholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency
            onChangeValue={(val) => {
              setPrice(val);
              setErrorText("");
            }}
            onChangeCurrency={(currencyCode) => {
              setCurrency(currencyCode);
            }}
          />
          {hasReductionStep && (
            <div className={style.reduction_step_hint}>
              <HintIcon aria-hidden="true" />
              <span>{t("orderInfoExpeditor.bid.reductionStepHint", { step: stepPrice })}</span>
            </div>
          )}
        </div>

        <div className={style.footer}>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("common.cancel")}
              isNavy
              isWhite
              onClick={handleClose}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.update")}
              isNavy
              onClick={handleUpdate}
              isLoading={isLoading}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpdateBidModal;
